import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { AiSearchOption, ResolvedAiSearchOption } from '../types/responses/AiSearchOptions';

const DEFAULT_PROJECT_ID = '800118750222';
const DEFAULT_LOCATION = 'global';
const DEFAULT_COLLECTION_ID = 'default_collection';
const DEFAULT_ENGINE_ID = 'c1b-ai-search_1777932380486';
const DEFAULT_DATASTORE_ID = '';
const DEFAULT_SERVING_CONFIG_ID = 'default_search';
const DEFAULT_MODEL_VERSION = 'stable';
const DEFAULT_SUMMARY_RESULT_COUNT = 5;
const DEFAULT_RESULTS_PATH = 'search';
const DEFAULT_PREAMBLE = 'Given the conversation between a user and a helpful assistant and some search results, create a final answer for the assistant. The answer should use all relevant information from the search results, not introduce any additional information, and use exactly the same words as the search results when possible. The assistant\'s answer should be no more than 10 sentences. The user is a member of the general public who doesn\'t have in-depth knowledge of the subject matter. The assistant should avoid using specialized knowledge, and instead answer in a non-technical manner that anyone can understand.';

const parseBoolean = (value: any, fallback: boolean): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }
    return fallback;
};


const normalizeRoutePath = (value: string): string => (value ?? '').trim().replace(/\/$/, '');

const normalizeSearchOption = (option : AiSearchOption) => {
    const source = option ? option : {};
    const parsedSummaryCount = Number(source.summaryResultCount);

    return {
        routePath: normalizeRoutePath(source.routePath),
        searchResultsPath: source.searchResultsPath ?? ''.trim(),
        projectId: source.projectId ?? ''.trim(),
        location: source.location ?? ''.trim(),
        collectionId: source.collectionId ?? ''.trim(),
        engineId: source.engineId ?? ''.trim(),
        dataStoreId: source.dataStoreId ?? ''.trim(),
        servingConfigId: source.servingConfigId ?? ''.trim(),
        modelVersion: source.modelVersion ?? ''.trim(),
        preamble: source.preamble ?? ''.trim(),
        summaryResultCount: Number.isInteger(parsedSummaryCount) ? parsedSummaryCount : undefined,
        enabled: parseBoolean(source.enabled, true),
        isDefault: parseBoolean(source.isDefault, false),
    };
};

export const getAiSearchOptions = (): AiSearchOption[] => {
    const rawValue = PathUtils.getMetaPropertyValue('aiSearchOptions');
    if (!rawValue) {
        return [];
    }

    try {
        const parsed = JSON.parse(rawValue);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.map(normalizeSearchOption);
    } catch {
        return [];
    }
};

const getBestMatchingOption = (pathname: string, options: AiSearchOption[]): AiSearchOption | null => {
    const normalizedPath = normalizeRoutePath(pathname);
    const activeOptions = options.filter((option) => option.enabled !== false);

    const directMatches = activeOptions.filter((option: AiSearchOption) => {
        const routePath = normalizeRoutePath(option.routePath);
        return Boolean(routePath) && normalizedPath.includes(routePath);
    });

    if (directMatches.length) {
        return directMatches.sort((a, b) => normalizeRoutePath(b.routePath).length - normalizeRoutePath(a.routePath).length)[0];
    }

    const defaults = activeOptions.filter((option) => option.isDefault);
    if (defaults.length) {
        return defaults[0];
    }

    return null;
};

export const resolveAiSearchOption = (pathname: string): ResolvedAiSearchOption => {
    const resolved = getBestMatchingOption(pathname, getAiSearchOptions()) ?? {};

    return {
        projectId: resolved.projectId || DEFAULT_PROJECT_ID,
        location: resolved.location || DEFAULT_LOCATION,
        collectionId: resolved.collectionId || DEFAULT_COLLECTION_ID,
        engineId: resolved.engineId || DEFAULT_ENGINE_ID,
        dataStoreId: resolved.dataStoreId || DEFAULT_DATASTORE_ID,
        servingConfigId: resolved.servingConfigId || DEFAULT_SERVING_CONFIG_ID,
        modelVersion: resolved.modelVersion || DEFAULT_MODEL_VERSION,
        preamble: resolved.preamble || DEFAULT_PREAMBLE,
        summaryResultCount: resolved.summaryResultCount && resolved.summaryResultCount > 0
            ? resolved.summaryResultCount
            : DEFAULT_SUMMARY_RESULT_COUNT,
        searchResultsPath: resolved.searchResultsPath || DEFAULT_RESULTS_PATH,
    };
};
